import { reactive } from 'vue';
import teamPlayers from './team.js';

export const store = reactive({
  match: {
    metadata: {
      teamName: 'Moss Side Jaguars',
      date: '',
      homeAway: '',
      opposition: '',
      competition: '',
      venue: '',
      round: ''
    },
    roster: [],
    sets: [],
    currentSetIndex: 0,
    setsWon: 0,
    setsLost: 0,
    finished: false,
    winner: null
  },
  currentLineup: Array(6).fill(null).map((_, index) => ({
    rotationPosition: index + 1,
    player: null,
    roleCode: null
  })),
  currentLibero: null, // Active libero for the current set
  createEmptySet(id) {
    return {
      id,
      startingLineup: [],
      startingLibero: null,
      points: [],
      score: { won: 0, lost: 0 },
      finished: false,
      winner: null
    };
  },
  getCurrentSet() {
    return this.match.sets[this.match.currentSetIndex];
  },
  setMatchMetadata(metadata) {
    this.match.metadata = { ...this.match.metadata, ...metadata };
  },
  setMatchRoster(roster) {
    this.match.roster = roster;
    if (!this.match.sets.length) {
      this.match.sets = [this.createEmptySet(1)];
      this.match.currentSetIndex = 0;
    }
  },
  setCurrentLineup(players, libero) {
    // console.log('setCurrentLineup(players)');
    this.currentLineup = players;
    this.currentLibero = libero; // Set libero
    const currentSet = this.getCurrentSet();
    if (currentSet) {
      currentSet.startingLineup = players;
      currentSet.startingLibero = libero;
    }
  },
  resetCurrentLineup() {
    this.currentLineup = Array(6).fill(null).map((_, index) => ({
      rotationPosition: index + 1,
      player: null,
      roleCode: null
    }));
    this.currentLibero = null;
  },
  resetMatchState() {
    this.match = {
      metadata: {
        teamName: 'Moss Side Jaguars',
        date: '',
        homeAway: '',
        opposition: '',
        competition: '',
        venue: '',
        round: ''
      },
      roster: [],
      sets: [this.createEmptySet(1)],
      currentSetIndex: 0,
      setsWon: 0,
      setsLost: 0,
      finished: false,
      winner: null
    };
    this.resetCurrentLineup();
  },
  hydrateFromStorage(saved) {
    if (!saved) {
      return;
    }
    const match = saved.match || {};
    const metadata = match.metadata || {};
    const teamPlayerById = new Map(teamPlayers.map(player => [player.id, player]));

    // Ensure teamName defaults to 'Moss Side Jaguars' if empty
    this.match.metadata = {
      teamName: metadata.teamName || 'Moss Side Jaguars',
      date: metadata.date || '',
      homeAway: metadata.homeAway || '',
      opposition: metadata.opposition || '',
      competition: metadata.competition || '',
      venue: metadata.venue || '',
      round: metadata.round || ''
    };
    const roster = Array.isArray(match.roster) ? match.roster : [];
    const liberos = Array.isArray(match.liberos) ? match.liberos : [];
    const rosterById = new Map();
    roster.forEach(player => {
      if (player && player.id !== undefined) {
        rosterById.set(player.id, { ...player, playerType: player.playerType || 'regular' });
      }
    });
    liberos.forEach(player => {
      if (player && player.id !== undefined) {
        rosterById.set(player.id, { ...player, playerType: 'libero' });
      }
    });
    rosterById.forEach((player, id) => {
      if (player && !player.photo) {
        const teamPlayer = teamPlayerById.get(id);
        if (teamPlayer && teamPlayer.photo) {
          rosterById.set(id, { ...player, photo: teamPlayer.photo });
        }
      }
    });
    this.match.roster = Array.from(rosterById.values());
    this.match.sets = Array.isArray(match.sets) && match.sets.length
      ? match.sets
      : [this.createEmptySet(1)];
    this.match.currentSetIndex = typeof match.currentSetIndex === 'number'
      ? match.currentSetIndex
      : 0;
    if (typeof match.setsWon === 'number' && typeof match.setsLost === 'number') {
      this.match.setsWon = match.setsWon;
      this.match.setsLost = match.setsLost;
    } else {
      this.match.setsWon = this.match.sets.filter(set => set.winner === 'won').length;
      this.match.setsLost = this.match.sets.filter(set => set.winner === 'lost').length;
    }
    this.match.finished = Boolean(match.finished);
    this.match.winner = match.winner || null;

    if (this.match.currentSetIndex >= this.match.sets.length) {
      this.match.currentSetIndex = 0;
    }

    if (Array.isArray(saved.currentLineup) && saved.currentLineup.length) {
      this.currentLineup = saved.currentLineup;
    } else if (Array.isArray(saved.rotationLineup) && saved.rotationLineup.length) {
      this.currentLineup = saved.rotationLineup;
    } else {
      this.resetCurrentLineup();
    }

    this.currentLibero = saved.currentLibero || saved.libero || null;
    this.normalizePlayerReferences();
  },
  normalizePlayerReferences() {
    const rosterMap = new Map();
    this.match.roster.forEach(player => {
      rosterMap.set(player.id, player);
    });

    const resolvePlayerRef = (player) => {
      if (!player) {
        return player;
      }
      if (typeof player === 'string') {
        return player;
      }
      const playerId = player.id ?? player.playerId ?? null;
      if (playerId !== null && rosterMap.has(playerId)) {
        return rosterMap.get(playerId);
      }
      return player;
    };

    this.currentLineup = this.currentLineup.map(item => {
      if (!item.player) {
        return item;
      }
      return { ...item, player: resolvePlayerRef(item.player) };
    });

    if (this.currentLibero) {
      this.currentLibero = rosterMap.get(this.currentLibero.id) || this.currentLibero;
    }

    this.match.sets = this.match.sets.map(set => {
      const nextSet = { ...set };
      if (Array.isArray(set.startingLineup)) {
        nextSet.startingLineup = set.startingLineup.map(item => {
          if (!item.player) {
            return item;
          }
          return { ...item, player: resolvePlayerRef(item.player) };
        });
      }
      if (set.startingLibero) {
        nextSet.startingLibero = rosterMap.get(set.startingLibero.id) || set.startingLibero;
      } else if (set.libero) {
        nextSet.startingLibero = rosterMap.get(set.libero.id) || set.libero;
        delete nextSet.libero;
      }
      if (Array.isArray(set.points)) {
        nextSet.points = set.points.map(point => this.hydratePointReferences(point, rosterMap));
      }
      return nextSet;
    });
  },
  hydratePointReferences(point, rosterMapOverride = null) {
    if (!point) {
      return point;
    }
    const rosterMap = rosterMapOverride || new Map(this.match.roster.map(player => [player.id, player]));
    const resolvePlayerRef = (player) => {
      if (!player) {
        return player;
      }
      if (typeof player === 'string') {
        return player;
      }
      const playerId = player.id ?? player.playerId ?? null;
      if (playerId !== null && rosterMap.has(playerId)) {
        return rosterMap.get(playerId);
      }
      return player;
    };
    const nextPoint = { ...point };
    if (nextPoint.serveAction) {
      nextPoint.serveAction = {
        ...nextPoint.serveAction,
        player: resolvePlayerRef(nextPoint.serveAction.player)
      };
    }
    if (nextPoint.receiveAction) {
      nextPoint.receiveAction = {
        ...nextPoint.receiveAction,
        player: resolvePlayerRef(nextPoint.receiveAction.player)
      };
    }
    if (nextPoint.scoreAction) {
      nextPoint.scoreAction = {
        ...nextPoint.scoreAction,
        player: resolvePlayerRef(nextPoint.scoreAction.player),
        secondPlayer: resolvePlayerRef(nextPoint.scoreAction.secondPlayer)
      };
    }
    return nextPoint;
  },
  rotatePlayers() {
    // console.log('rotatePlayers()');
    if (this.currentLineup.length === 6) {
      this.currentLineup.forEach(player => {
        if (player.rotationPosition === 1) {
          player.rotationPosition = 6;
        } else {
          player.rotationPosition -= 1;
        }
      });
    }
  },
  addPointToCurrentSet(point) {
    const currentSet = this.getCurrentSet();
    if (!currentSet) {
      return;
    }
    currentSet.points.push(point);
    currentSet.score = { ...point.currentScore };
  },
  endCurrentSet(winner) {
    const currentSet = this.getCurrentSet();
    if (!currentSet) {
      return;
    }
    if (currentSet.finished) {
      return;
    }
    currentSet.finished = true;
    currentSet.winner = winner;
    if (winner === 'won') {
      this.match.setsWon += 1;
    } else if (winner === 'lost') {
      this.match.setsLost += 1;
    }
  },
  startNextSet() {
    const nextId = this.match.sets.length + 1;
    this.match.sets.push(this.createEmptySet(nextId));
    this.match.currentSetIndex = this.match.sets.length - 1;
  },
  setMatchFinished(winner) {
    this.match.finished = true;
    this.match.winner = winner;
  }
});